import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';
import { AIService } from '../services';
import { SyntaxLensIcon } from '../assets';
import { UpdatedRustDemo, UpdatedJavaScriptDemo } from './UpdatedExamples';
import { SyntaxLensWidget } from './SyntaxLensWidget';
import { useZenSettings } from '../hooks/useZenSettings';
import { useProviders } from '../hooks/useProviders';
import { checkBackendHealth } from '../utils/healthCheck';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileDemoList from './MobileDemoList';
import MicroHero from './MicroHero';
import captions from '../data/captions.json';

// Re-export for backward compatibility
export { SyntaxLensWidget };



// Demo Components with both static and dynamic capabilities
const RustDemo = () => (
  <SyntaxLensWidget className="bg-gray-900 rounded-xl p-6" sourceLanguage="C" targetLanguage="Rust" skipBackendCheck={true}>
    <div className="relative">
      <div className="absolute top-0 right-0 bg-gray-800/80 text-gray-400 text-xs font-mono px-3 py-1 rounded-bl-lg">
        rust
      </div>
      <UpdatedRustDemo />
    </div>

    <div className="mt-4 pt-4 border-t border-gray-700/50">
      <p className="text-gray-400 text-sm text-center">C to Rust: Memory Management &amp; Safety</p>
    </div>
  </SyntaxLensWidget>
);

const JavaScriptDemo = () => (
  <SyntaxLensWidget className="bg-gray-900 rounded-xl p-6" sourceLanguage="Python" targetLanguage="JavaScript" skipBackendCheck={true}>
    <div className="relative">
      <div className="absolute top-0 right-0 bg-gray-800/80 text-gray-400 text-xs font-mono px-3 py-1 rounded-bl-lg">
        javascript
      </div>
      <UpdatedJavaScriptDemo />
    </div>

    <div className="mt-4 pt-4 border-t border-gray-700/50">
      <p className="text-gray-400 text-sm text-center">Python to JavaScript: Async &amp; Classes</p>
    </div>
  </SyntaxLensWidget>
);

// Enhanced Dynamic Generator with AI Integration
const DynamicGenerator = () => {
  const [form, setForm] = useState({
    sourceLanguage: 'C',
    targetLanguage: 'Rust',
    concept: 'variables'
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [aiService, setAIService] = useState<AIService | null>(null);
  
  // Zen settings for provider selection
  const { getPreferredProvider } = useZenSettings();
  const { providers } = useProviders();

  const concepts = [
    'variables', 'functions', 'loops', 'conditionals', 'arrays', 
    'structs', 'error handling', 'memory management', 'modules', 'classes'
  ];

  const languages = [
    'C', 'C++', 'Python', 'JavaScript', 'Rust', 'Go', 'Java', 'TypeScript'
  ];

  useEffect(() => {
    const checkBackend = async () => {
      const available = await checkBackendHealth();
      if (available) {
        setAIService(new AIService());
      }
    };
    checkBackend();
  }, []);

  const generateCode = async () => {
    if (!aiService) {
      alert('Backend server is not available. Please start the backend server first.');
      return;
    }

    setIsGenerating(true);
    
    try {
      // Get preferred provider from Zen settings
      const preferredProvider = getPreferredProvider(providers);
      
      const result = await aiService.generateComparison(
        form.sourceLanguage,
        form.targetLanguage,
        form.concept,
        undefined, // codeSnippet
        preferredProvider // provider
      );
      
      setGeneratedContent(result);
    } catch (error) {
      console.error('Error generating code:', error);
      alert('Error generating code. Please check that the backend server is running.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (generatedContent) {
    return (
      <div
        data-testid="generator-result-view"
        data-state="showing-results"
        className="slens-demo-box"
      >
        <div className="mb-4">
          <button
            data-testid="generator-button-back"
            aria-label="Go back to generator form"
            onClick={() => setGeneratedContent(null)}
            className="text-brand-primary hover:opacity-80 mb-4 text-slens-sm font-medium"
          >
            ← Back to Generator
          </button>
        </div>
        
        <SyntaxLensWidget 
          className="bg-gray-900 rounded-xl p-6"
          sourceLanguage={form.sourceLanguage}
          targetLanguage={form.targetLanguage}
        >
          <div className="mb-4">
            <h3 className="text-2xl font-bold text-white mb-2">
              {generatedContent.title || `${form.sourceLanguage} to ${form.targetLanguage}: ${form.concept}`}
            </h3>
            <p className="text-gray-300">
              {generatedContent.description || "AI-generated comparison"}
            </p>
          </div>
          
          <div className="font-mono text-sm space-y-2 text-left">
            <style dangerouslySetInnerHTML={{
              __html: `
                /* Ensure generated content matches static demo styling with dark theme */
                .bg-gray-900 .text-gray-500 { color: #6b7280 !important; }
                .bg-gray-900 .text-gray-300 { color: #d1d5db !important; }
                .bg-gray-900 .flex { display: flex !important; }
                .bg-gray-900 .items-center { align-items: center !important; }
                .bg-gray-900 .w-8 { width: 2rem !important; }
                .bg-gray-900 .text-right { text-align: right !important; }
                .bg-gray-900 .mr-4 { margin-right: 1rem !important; }
                .bg-gray-900 .flex-1 { flex: 1 1 0% !important; }
                
                /* Apply syntax highlighting to generated content (all elements) */
                .bg-gray-900 .keyword { color: #ff6b6b !important; font-weight: 600; }
                .bg-gray-900 .type-keyword { color: #4ecdc4 !important; font-weight: 500; }
                .bg-gray-900 .function, .bg-gray-900 .method { color: #45b7d1 !important; font-weight: 500; }
                .bg-gray-900 .variable { color: #ecf0f1 !important; }
                .bg-gray-900 .operator { color: #a8e6cf !important; font-weight: 500; }
                .bg-gray-900 .string { color: #feca57 !important; }
                .bg-gray-900 .number { color: #ff9ff3 !important; }
                .bg-gray-900 .comment { color: #95a5a6 !important; font-style: italic; }
                .bg-gray-900 .bracket { color: #ffa726 !important; font-weight: 600; }

                /* Enhanced highlighting for hoverable elements */
                .bg-gray-900 .keyword.hoverable { color: #ff6b6b !important; font-weight: 700; text-shadow: 0 0 2px rgba(255, 107, 107, 0.3); }
                .bg-gray-900 .type-keyword.hoverable { color: #4ecdc4 !important; font-weight: 600; text-shadow: 0 0 2px rgba(78, 205, 196, 0.3); }
                .bg-gray-900 .function.hoverable, .bg-gray-900 .method.hoverable { color: #45b7d1 !important; font-weight: 600; text-shadow: 0 0 2px rgba(69, 183, 209, 0.3); }
                .bg-gray-900 .variable.hoverable { color: #ecf0f1 !important; font-weight: 500; text-shadow: 0 0 2px rgba(236, 240, 241, 0.3); }
                .bg-gray-900 .operator.hoverable { color: #a8e6cf !important; font-weight: 600; text-shadow: 0 0 2px rgba(168, 230, 207, 0.3); }
                .bg-gray-900 .string.hoverable { color: #feca57 !important; font-weight: 500; text-shadow: 0 0 2px rgba(254, 202, 87, 0.3); }
                .bg-gray-900 .number.hoverable { color: #ff9ff3 !important; }
                .bg-gray-900 .comment.hoverable { color: #95a5a6 !important; font-style: italic; }
                .bg-gray-900 .bracket.hoverable { color: #ffa726 !important; font-weight: 600; }
                
                /* Force left alignment for all generated code content */
                .bg-gray-900 .font-mono, 
                .bg-gray-900 .text-left { 
                  text-align: left !important; 
                  justify-content: flex-start !important;
                }
                .bg-gray-900 .space-y-2 > * { 
                  margin-left: 0 !important; 
                  margin-right: auto !important;
                  justify-content: flex-start !important;
                }
                
                /* Ensure flex containers don't center content */
                .bg-gray-900 .flex.items-center {
                  justify-content: flex-start !important;
                }
              `
            }} />
            <div dangerouslySetInnerHTML={{ __html: generatedContent.html }} />
          </div>
        </SyntaxLensWidget>
      </div>
    );
  }

  return (
    <div
      data-testid="generator-form-view"
      data-state="editing"
      className="slens-demo-box"
    >
      <h3 className="slens-demo-title mb-4">
        Generate Custom Comparison with AI
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label
            htmlFor="slens-generator-source-language"
            className="block text-slens-sm font-medium text-slens-text mb-2"
          >
            I know:
          </label>
          <select
            id="slens-generator-source-language"
            data-testid="generator-select-source"
            aria-label="Select the programming language you already know"
            value={form.sourceLanguage}
            onChange={(e) => setForm({...form, sourceLanguage: e.target.value})}
            className="w-full p-2 border border-slens-gray-200 rounded-lg bg-slens-white text-slens-text focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
          >
            {languages.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="slens-generator-target-language"
            className="block text-slens-sm font-medium text-slens-text mb-2"
          >
            Teach me:
          </label>
          <select
            id="slens-generator-target-language"
            data-testid="generator-select-target"
            aria-label="Select the programming language you want to learn"
            value={form.targetLanguage}
            onChange={(e) => setForm({...form, targetLanguage: e.target.value})}
            className="w-full p-2 border border-slens-gray-200 rounded-lg bg-slens-white text-slens-text focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
          >
            {languages.filter(lang => lang !== form.sourceLanguage).map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="slens-generator-concept"
            className="block text-slens-sm font-medium text-slens-text mb-2"
          >
            Concept:
          </label>
          <select
            id="slens-generator-concept"
            data-testid="generator-select-concept"
            aria-label="Select the programming concept to compare"
            value={form.concept}
            onChange={(e) => setForm({...form, concept: e.target.value})}
            className="w-full p-2 border border-slens-gray-200 rounded-lg bg-slens-white text-slens-text focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
          >
            {concepts.map(concept => (
              <option key={concept} value={concept}>{concept}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        data-testid="generator-button-generate"
        data-state={isGenerating ? 'generating' : 'idle'}
        aria-label={`Generate syntax comparison from ${form.sourceLanguage} to ${form.targetLanguage}`}
        onClick={generateCode}
        disabled={isGenerating || !aiService}
        className="bg-orange-500 text-white px-6 py-2 rounded-md font-semibold shadow-sm hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full max-w-sm mx-auto"
      >
        {isGenerating ? (
          <div className="flex items-center justify-center gap-2 text-sm animate-pulse">
            <Sparkles className="w-4 h-4 text-orange-300" />
            <span>Generating with AI...</span>
          </div>
        ) : !aiService ? 'Backend Not Available' :
         `✨ Generate (${form.sourceLanguage} → ${form.targetLanguage})`}
      </button>
    </div>
  );
};

// Main Landing Page
const LandingPage = () => {
  const [activeDemo, setActiveDemo] = useState('rust');
  const isMobile = useIsMobile();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  /* --- Pure‑CSS sticky: no JS observer needed --- */

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  return (
    <div
      id="slens-landing-root"
      data-component="landing-page"
      className="bg-warm-bg min-h-screen relative"
    >
      {/* Theater Mode Backdrop - Blur and darken background when video plays */}
      {isVideoPlaying && (
        <div
          className="fixed inset-0 z-50 animate-fade-in"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            animation: 'fadeInBackdrop 0.8s ease-out forwards'
          }}
          onClick={(e) => {
            const video = document.querySelector('[data-testid="landing-video-intro"]') as HTMLVideoElement;
            if (video) video.pause();
          }}
        />
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fadeInBackdrop {
            0% {
              opacity: 0;
              backdrop-filter: blur(0px);
              -webkit-backdrop-filter: blur(0px);
            }
            100% {
              opacity: 1;
              backdrop-filter: blur(8px);
              -webkit-backdrop-filter: blur(8px);
            }
          }
        `
      }} />

      {/* Header */}
      <header
        id="slens-landing-header"
        data-section="header"
        className="slens-header bg-warm-bg"
      >
        <div className="slens-header-content px-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <img src={SyntaxLensIcon} alt="SyntaxLens" className="h-10 sm:h-12 w-10 sm:w-12" />
            <span className="text-slens-text font-bold text-xl sm:text-2xl">SyntaxLens</span>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <nav className="hidden md:flex items-center gap-6">
            </nav>
            {/* Only show playground button on desktop */}
            {!isMobile && (
              <button
                data-testid="playground-button"
                aria-label="Open playground to create custom syntax comparisons"
                onClick={() => window.location.href = '?page=zen-simple'}
                className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-md px-5 py-2.5 font-semibold shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-200"
              >
                <Sparkles className="w-4 h-4" />
                <span>{captions.cta.primary}</span>
                <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full font-medium">NEW</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        id="slens-landing-hero"
        data-section="hero"
        className="slens-hero bg-warm-bg"
      >
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="slens-hero-headline" dangerouslySetInnerHTML={{ __html: captions.homepage.main }}>
          </h1>
          <p className="slens-hero-description" dangerouslySetInnerHTML={{ __html: captions.homepage.subtitle }}>
          </p>

          {/* Mobile: Context explainer before demos */}
          {isMobile && <MicroHero />}

          {/* Intro Video - Strategic placement between hero and demos */}
          {!isMobile && (
            <div
              id="slens-intro-video-container"
              data-section="intro-video"
              className={`mt-16 mb-12 max-w-4xl mx-auto transition-all duration-500 origin-center  ${
                isVideoPlaying ? 'relative z-[60] scale-[1.15] -translate-y-8' : 'relative'
              }`}
            >

              <div
                className="relative rounded-2xl overflow-hidden bg-slate-900 transition-all duration-300"
                style={{
                  boxShadow: isVideoPlaying
                    ? '0 40px 100px rgba(0, 0, 0, 0.6), 0 20px 50px rgba(0, 0, 0, 0.4)'
                    : '0 20px 60px rgba(0, 0, 0, 0.3), 0 10px 30px rgba(0, 0, 0, 0.2)'
                }}
                onMouseEnter={(e) => {
                  if (!isVideoPlaying) {
                    e.currentTarget.style.boxShadow = '0 30px 80px rgba(0, 0, 0, 0.4), 0 15px 40px rgba(0, 0, 0, 0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isVideoPlaying) {
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3), 0 10px 30px rgba(0, 0, 0, 0.2)';
                  }
                }}
              >
                {/* Subtle glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/5 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                {/* Aspect ratio container - 16:9 standard */}
                <div className="relative w-full group" style={{ paddingBottom: '56.25%' }}>
                  <video
                    data-testid="landing-video-intro"
                    aria-label="SyntaxLens introduction video demonstrating interactive syntax comparison features"
                    className="absolute top-0 left-0 w-full h-full object-contain transition-all duration-300"
                    style={{
                      filter: !isVideoPlaying ? 'blur(0px) brightness(0.6) contrast(1.1) saturate(0.4)' : 'blur(0px) brightness(1)'
                    }}
                    controls
                    preload="metadata"
                    poster="/videos/syntaxlens-final-intro-thumbnail.jpg"
                    playsInline
                    onPlay={handleVideoPlay}
                    onPause={handleVideoPause}
                    onEnded={handleVideoPause}
                  >
                    <source src="/videos/syntaxlens-final-intro.mp4" type="video/mp4" />
                    <track
                      kind="captions"
                      src="/videos/syntaxlens-captions.vtt"
                      srcLang="en"
                      label="English"
                    />
                    Your browser does not support the video tag. Please use a modern browser to view this content.
                  </video>

                  {/* Professional Vignette Effect - darker edges, lighter center */}
                  {!isVideoPlaying && (
                    <div
                      className="absolute inset-0 pointer-events-none z-10"
                      style={{
                        background: 'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0, 0, 0, 0.3) 100%)'
                      }}
                    />
                  )}

                  {/* Duration Badge - Top Right */}
                  {!isVideoPlaying && (
                    <div className="absolute top-4 right-4 z-20 pointer-events-none">
                      <span className="inline-block px-3 py-1.5 bg-slate-900/80 backdrop-blur-sm text-white text-xs font-semibold rounded-md border border-slate-700/50">
                        1:08
                      </span>
                    </div>
                  )}

                  {/* Custom Play Button Overlay with Label */}
                  {!isVideoPlaying && (
                    <div className="play-button-wrapper absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-20">
                      {/* Play Button */}
                      <button
                        className="play-button relative flex items-center justify-center w-20 h-20 rounded-full bg-orange-500/90 backdrop-blur-sm border-2 border-orange-400 transition-all duration-300 group-hover:scale-110 group-hover:bg-orange-600 group-hover:border-orange-300 shadow-2xl pointer-events-auto cursor-pointer"
                        style={{
                          boxShadow: '0 8px 32px rgba(243, 112, 33, 0.4), 0 0 0 0 rgba(243, 112, 33, 0.3)',
                          animation: 'pulse-subtle 2s ease-in-out infinite'
                        }}
                        onClick={(e) => {
                          const video = e.currentTarget.closest('.group')?.querySelector('video');
                          video?.play();
                        }}
                        aria-label="Play video"
                      >
                        {/* Play triangle */}
                        <svg className="w-6 h-6 ml-1 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>

                        {/* Pulsing ring animation */}
                        <div
                          className="absolute inset-0 rounded-full border-2 border-orange-300/60"
                          style={{
                            animation: 'ping-slow 2s ease-in-out infinite'
                          }}
                        />
                      </button>

                      {/* Watch Quick Demo Label */}
                      <span
                        className="video-label mt-4 inline-block px-4 py-2 bg-slate-900/80 backdrop-blur-md text-sm font-semibold rounded-full border border-slate-700/50 shadow-lg"
                        style={{ color: 'rgb(252, 252, 249)' }}
                      >
                        Watch Quick Demo
                      </span>
                    </div>
                  )}
                </div>

                {/* Custom animations */}
                <style dangerouslySetInnerHTML={{
                  __html: `
                    @keyframes pulse-subtle {
                      0%, 100% {
                        transform: scale(1);
                        opacity: 0.9;
                      }
                      50% {
                        transform: scale(1.03);
                        opacity: 1;
                      }
                    }

                    @keyframes ping-slow {
                      0% {
                        transform: scale(1);
                        opacity: 0.8;
                      }
                      50% {
                        transform: scale(1.15);
                        opacity: 0.4;
                      }
                      100% {
                        transform: scale(1.3);
                        opacity: 0;
                      }
                    }
                  `
                }} />

                {/* Optional: Video caption/description */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 to-transparent p-4 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <p className="text-white text-sm font-medium text-center">
                    ▶ Watch: See how SyntaxLens makes learning new languages intuitive
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Demo Content - Conditional based on device */}
          {isMobile ? (
            /* Mobile: Card-based demo list */
            <div
              id="slens-demo-content"
              data-section="demo-content"
              className="max-w-6xl mx-auto px-4"
            >
              <MobileDemoList />
            </div>
          ) : (
            /* Desktop: Interactive tabs with hover demos */
            <>
              {/* Demo Navigation */}
              <div
                id="slens-demo-navigation"
                data-section="demo-navigation"
                role="tablist"
                aria-label="Demo selection"
                className="slens-nav-pills"
              >
                <div className="inline-flex gap-3 flex-wrap">
                  <button
                    id="slens-tab-rust"
                    role="tab"
                    data-testid="demo-tab-rust"
                    data-state={activeDemo === 'rust' ? 'active' : 'inactive'}
                    aria-selected={activeDemo === 'rust'}
                    aria-controls="slens-panel-rust"
                    aria-label="View C to Rust syntax comparison demo"
                    onClick={() => setActiveDemo('rust')}
                    className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                      activeDemo === 'rust'
                        ? 'bg-white text-slate-900 border-2 border-orange-500 shadow-md'
                        : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }`}
                  >
                    [C] → Rust
                  </button>

                  <button
                    id="slens-tab-javascript"
                    role="tab"
                    data-testid="demo-tab-javascript"
                    data-state={activeDemo === 'javascript' ? 'active' : 'inactive'}
                    aria-selected={activeDemo === 'javascript'}
                    aria-controls="slens-panel-javascript"
                    aria-label="View Python to JavaScript syntax comparison demo"
                    onClick={() => setActiveDemo('javascript')}
                    className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                      activeDemo === 'javascript'
                        ? 'bg-white text-slate-900 border-2 border-orange-500 shadow-md'
                        : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }`}
                  >
                    [Python] → JavaScript
                  </button>
                </div>
              </div>

              {/* Demo Content */}
              <div
                id="slens-demo-content"
                data-section="demo-content"
                className="max-w-6xl mx-auto"
              >
                {activeDemo === 'rust' && (
                  <div
                    id="slens-panel-rust"
                    role="tabpanel"
                    data-testid="demo-panel-rust"
                    data-state="active"
                    aria-labelledby="slens-tab-rust"
                    className="bg-warm-bg rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                  >
                    <div className="bg-warm-bg-dark px-6 py-4 border-b border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">See it in action</h3>
                      <p className="text-sm text-slate-600">Hover over highlighted elements • Navigate: ← → • Depth: 1 2 3 • Space to pin</p>
                    </div>
                    <div className="p-6">
                      <RustDemo />
                    </div>
                  </div>
                )}

                {activeDemo === 'javascript' && (
                  <div
                    id="slens-panel-javascript"
                    role="tabpanel"
                    data-testid="demo-panel-javascript"
                    data-state="active"
                    aria-labelledby="slens-tab-javascript"
                    className="bg-warm-bg rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                  >
                    <div className="bg-warm-bg-dark px-6 py-4 border-b border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">See it in action</h3>
                      <p className="text-sm text-slate-600">Hover over highlighted elements • Navigate: ← → • Depth: 1 2 3 • Space to pin</p>
                    </div>
                    <div className="p-6">
                      <JavaScriptDemo />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Mobile: Desktop mention section */}
      {isMobile && (
        <section className="bg-slate-50 border-t border-slate-200 py-8 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-slate-600 text-sm mb-3">
              💻 Want to create insights for your code?
            </p>
            <p className="text-slate-500 text-xs leading-relaxed">
              Visit SyntaxLens on a desktop or laptop computer to access the full creator playground.
            </p>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer
        id="slens-landing-footer"
        data-section="footer"
        className="slens-footer bg-warm-bg"
      >
        <div className="max-w-4xl mx-auto px-4">
          <p>🤖 Powered by AI • 🔬 Interactive Learning • ⚡ Real-time Explanations</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
